import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined } from '@ant-design/icons';
import { useState } from 'react';

import styled from '@emotion/styled';

import { commandKeyLabel, Kbd, openCommandMenu } from '../../../components/CommandMenu/CommandMenu';
import { ButtonLink } from '../../../components/Link/Link';
import HetheraLogo from '../../../components/CompanyLogo/HetheraLogo';
import StatusLight from '../../../components/StatusLight/StatusLight';
import { useInbox } from '../../../context/InboxContext';
import { NavBoxItem } from '../../../components/Link/Link';
import useOnLogout from '../../../hooks/auth/useOnLogout';
import { useAppSelector } from '../../../redux/store';
import { PAGE_GUIDES } from '../../../models/guides/pageGuides';
import { INBOX } from '../../../routes/routeConstants';
import { navGroups } from './navConfig';

const COLLAPSED_KEY = 'hethera:nav-collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

// Light sidebar (Vercel/Mercury): logo, a search box that opens the command
// menu, grouped links, and the signed-in admin at the bottom. Collapses to an
// icon rail instead of disappearing, and remembers that choice.
const Nav = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { logoutUser } = useOnLogout();
  const { authData } = useAppSelector((state) => state.auth);
  const username = authData?.user?.username ?? 'Admin';
  const { items } = useInbox();

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {}
      return !c;
    });
  };

  return (
    <Container className={collapsed ? 'collapsed' : ''} aria-label='Main navigation'>
      <Top>
        <ButtonLink to='/' className='logo' aria-label='Hethera home'>
          {collapsed ? <HetheraLogo wordmark={false} size={28} /> : <HetheraLogo size={26} />}
        </ButtonLink>
        <IconButton
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className='toggle'
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </IconButton>
      </Top>

      <SearchTrigger type='button' onClick={openCommandMenu} title={`Search (${commandKeyLabel})`}>
        <SearchOutlined />
        <span className='text'>Search</span>
        <Kbd className='kbd'>{commandKeyLabel}</Kbd>
      </SearchTrigger>

      <Groups>
        {navGroups.map((group, index) => (
          <Group key={group.label ?? index}>
            {group.label && <GroupLabel>{group.label}</GroupLabel>}
            {group.items.map((nav) => (
              <NavBoxItem
                path={nav.path}
                key={nav.title}
                icon={nav.icon}
                hint={PAGE_GUIDES[nav.path]?.summary}
                badge={nav.path === INBOX ? items.length : undefined}
              >
                {nav.title}
              </NavBoxItem>
            ))}
          </Group>
        ))}
      </Groups>

      <StatusLight />

      <Account>
        <Avatar aria-hidden>{username.slice(0, 1).toUpperCase()}</Avatar>
        <div className='who'>
          <strong>{username}</strong>
          <span>Administrator</span>
        </div>
        <IconButton onClick={logoutUser} aria-label='Sign out' title='Sign out' className='logout'>
          <LogoutOutlined />
        </IconButton>
      </Account>
    </Container>
  );
};

export default Nav;

const Container = styled.nav`
  position: sticky;
  top: 0;
  z-index: 20;
  flex: none;
  display: flex;
  flex-direction: column;
  width: 248px;
  height: 100vh;
  background: var(--panel);
  border-right: 1px solid var(--line);
  transition: width 0.2s ease;
  overflow: hidden;

  @media (max-width: 884px) {
    display: none;
  }

  /* Icon rail */
  &.collapsed {
    width: 64px;

    & .nav-label,
    & .text,
    & .kbd,
    & .who,
    & .logout,
    & .status-trigger .text {
      display: none;
    }
    & .status-trigger {
      justify-content: center;
      padding: 0;
    }
    /* The Inbox count becomes a dot on the icon. */
    & .nav-badge {
      position: absolute;
      top: 4px;
      right: 10px;
      min-width: 8px;
      width: 8px;
      height: 8px;
      padding: 0;
      font-size: 0;
      background: var(--red);
    }
    & a[title] {
      justify-content: center;
      padding: 0;
    }
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 64px;
  padding: 0 12px 0 20px;
  flex: none;

  & .logo {
    display: flex;
  }

  .collapsed & {
    flex-direction: column;
    justify-content: center;
    height: auto;
    padding: 16px 0 8px;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: none;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  font-size: 15px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: var(--hover);
    color: var(--ink);
  }
`;

const SearchTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 10px 12px;
  padding: 0 8px 0 10px;
  height: 34px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: #ffffff;
  color: var(--muted);
  font-size: 14px;
  cursor: pointer;
  box-shadow: var(--shadow-xs);
  transition: border-color 0.15s ease;

  & .text {
    flex: 1;
    text-align: left;
  }

  &:hover {
    border-color: var(--line-2);
    color: var(--ink);
  }

  .collapsed & {
    justify-content: center;
    padding: 0;
  }
`;

const Groups = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 12px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupLabel = styled.div`
  padding: 0 20px 4px;
  font-size: 12px;
  font-weight: 500;
  color: #7a8279;
  white-space: nowrap;

  .collapsed & {
    height: 1px;
    margin: 0 18px 6px;
    padding: 0;
    overflow: hidden;
    color: transparent;
    background: var(--line);
  }
`;

const Account = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px 12px 16px;
  border-top: 1px solid var(--line);
  flex: none;

  & .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  & .who strong {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & .who span {
    font-size: 12px;
    color: #7a8279;
  }

  .collapsed & {
    justify-content: center;
    padding: 12px 0;
  }
`;

const Avatar = styled.div`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 999px;
  background: var(--accent);
  color: var(--ink);
  font-size: 13px;
  font-weight: 700;
`;
